// A viewport is the stage transform: the screen position of the image's origin
// and the zoom (screen px per image px). Sizes are the board's, in screen px.
export const MAX_ZOOM = 4;
const PADDING = 16;
const MARGIN = 64;

const NO_INSETS = { top: 0, right: 0, bottom: 0, left: 0 };
// Below this the chrome leaves too little room, and the scene fits the board.
const MIN_ROOM = 160;

// What the board's chrome leaves uncovered (`insets`, in screen px from each
// edge), or the whole board when that is too little.
export function freeRect(size, insets = NO_INSETS) {
	const roomy = size.width - insets.left - insets.right >= MIN_ROOM && size.height - insets.top - insets.bottom >= MIN_ROOM;
	const { top, right, bottom, left } = roomy ? insets : NO_INSETS;
	return { x: left, y: top, width: size.width - left - right, height: size.height - top - bottom };
}

// Fits the scene inside what the chrome leaves uncovered, centred there. A box may start past the image's origin
// (`x`, `y`), as a map's resize preview does.
export function fitViewport(scene, size, insets = NO_INSETS) {
	const { x, y, width, height } = freeRect(size, insets);
	const zoom = Math.min((width - 2 * PADDING) / scene.width_px, (height - 2 * PADDING) / scene.height_px);
	return { x: x + (width - scene.width_px * zoom) / 2 - (scene.x ?? 0) * zoom, y: y + (height - scene.height_px * zoom) / 2 - (scene.y ?? 0) * zoom, zoom };
}

// The same zoom, panned just enough to bring an image point `margin` px
// inside what the chrome leaves uncovered.
export function revealPoint(viewport, point, size, insets = NO_INSETS, margin = 0) {
	const screen = toScreen(viewport, point);
	const shift = (at, low, high) => (high < low ? (low + high) / 2 - at : Math.max(low - at, Math.min(high - at, 0)));
	const dx = shift(screen.x, insets.left + margin, size.width - insets.right - margin);
	const dy = shift(screen.y, insets.top + margin, size.height - insets.bottom - margin);
	return dx || dy ? { ...viewport, x: viewport.x + dx, y: viewport.y + dy } : viewport;
}

export function zoomLimits(scene, size) {
	const fit = fitViewport(scene, size).zoom;
	return { min: Math.min(fit / 2, 1), max: Math.max(MAX_ZOOM, fit) };
}

// Keeps the image point under `point` where it is while the zoom changes.
export function zoomAt(viewport, point, zoom) {
	const anchor = toImage(viewport, point);
	return { x: point.x - anchor.x * zoom, y: point.y - anchor.y * zoom, zoom };
}

// Keeps the zoom within limits and at least a margin of the scene on screen.
export function clampViewport(viewport, scene, size) {
	const { min, max } = zoomLimits(scene, size);
	const zoom = Math.min(Math.max(viewport.zoom, min), max);
	const next = zoom === viewport.zoom ? viewport : zoomAt(viewport, { x: size.width / 2, y: size.height / 2 }, zoom);
	const margin = (length, extent) => Math.min(MARGIN, length * zoom, extent / 2);
	const mx = margin(scene.width_px, size.width);
	const my = margin(scene.height_px, size.height);
	const ox = (scene.x ?? 0) * zoom;
	const oy = (scene.y ?? 0) * zoom;
	return {
		x: clamp(next.x, mx - scene.width_px * zoom - ox, size.width - mx - ox),
		y: clamp(next.y, my - scene.height_px * zoom - oy, size.height - my - oy),
		zoom,
	};
}

export function visibleRect(viewport, size) {
	return { x: -viewport.x / viewport.zoom, y: -viewport.y / viewport.zoom, width: size.width / viewport.zoom, height: size.height / viewport.zoom };
}

export function toImage(viewport, point) {
	return { x: (point.x - viewport.x) / viewport.zoom, y: (point.y - viewport.y) / viewport.zoom };
}

export function toScreen(viewport, point) {
	return { x: viewport.x + point.x * viewport.zoom, y: viewport.y + point.y * viewport.zoom };
}

function clamp(value, low, high) {
	return Math.min(Math.max(value, low), high);
}

// The table's view (a scene's table_view) names the image point at the centre
// and the zoom for a screen whose shorter side is TABLE_SIDE px, so a phone
// and a TV show the same stretch of map along their shorter sides.
const TABLE_SIDE = 1000;

// The master's view as the table's: what shows clear of the chrome.
export function tableViewOf(viewport, size, insets = NO_INSETS) {
	const free = freeRect(size, insets);
	const centre = toImage(viewport, { x: free.x + free.width / 2, y: free.y + free.height / 2 });
	return { x: Math.round(centre.x), y: Math.round(centre.y), zoom: Number(((viewport.zoom * TABLE_SIDE) / Math.min(free.width, free.height)).toFixed(4)) };
}

export function tableCentre(view, size) {
	if (!Number.isFinite(view?.x) || !Number.isFinite(view?.y) || !(view?.zoom > 0)) return null;
	return { cx: view.x, cy: view.y, zoom: (view.zoom * Math.min(size.width, size.height)) / TABLE_SIDE };
}
